<script>
import { mapActions } from 'vuex';

import Catalogue from '@/services/catalogue';
import request from '@/services/request';

import ModalPaymentAddOnComponent
    from '@/components/bill_external/ModalPaymentAddOnComponent.vue';

export default {

    name: 'modal-show-bill-external-debt',

    components: {
        ModalPaymentAddOnComponent
    },

    data() {
        return {
            catalogue: new Catalogue(),

            errors: {},

            load: false,

            send: false,

            cancellingBillId: null,

            debt: {},

            bills: [],
        };
    },

    methods: {

        /*
         * ========================================
         * HELPERS GENERALES
         * ========================================
         */

        numeric(value) {
            const number = Number(value);

            return Number.isFinite(number)
                ? number
                : 0;
        },

        parseInvoiceJson(bill) {
            if (
                !bill ||
                !bill.json
            ) {
                return {};
            }

            if (
                typeof bill.json === 'object'
            ) {
                return bill.json;
            }

            try {
                return JSON.parse(
                    bill.json
                );
            } catch (error) {
                return {};
            }
        },

        getNestedValue(object, path, fallback = null) {
            if (
                !object ||
                !path
            ) {
                return fallback;
            }

            const value = path
                .split('.')
                .reduce(
                    (current, key) => {
                        if (
                            current === null ||
                            current === undefined
                        ) {
                            return undefined;
                        }

                        return current[key];
                    },
                    object
                );

            return (
                value === null ||
                value === undefined ||
                value === ''
            )
                ? fallback
                : value;
        },

        firstValue(values, fallback = '') {
            const value = values.find(item => {
                return (
                    item !== null &&
                    item !== undefined &&
                    item !== ''
                );
            });

            return (
                value === undefined
            )
                ? fallback
                : value;
        },


        /*
         * ========================================
         * INFORMACIÓN DE FACTURA
         * ========================================
         */

        getInvoiceJson(bill) {
            return this.parseInvoiceJson(
                bill
            );
        },

        getInvoiceUuid(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                bill && bill.uuid,
                this.getNestedValue(
                    json,
                    'uuid'
                ),
                this.getNestedValue(
                    json,
                    'id'
                ),
            ], 'Sin UUID');
        },

        getInvoiceSeries(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                bill && bill.series,
                this.getNestedValue(
                    json,
                    'series'
                ),
            ], '—');
        },

        getInvoiceFolio(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                bill && bill.folio_number,
                bill && bill.folio,
                this.getNestedValue(
                    json,
                    'folio_number'
                ),
                this.getNestedValue(
                    json,
                    'folio'
                ),
            ], '—');
        },

        getCfdiVersion(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                bill && bill.cfdi_version,
                this.getNestedValue(
                    json,
                    'cfdi_version'
                ),
            ], '4.0');
        },

        getInvoiceDate(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                bill && bill.created_at,
                this.getNestedValue(
                    json,
                    'created_at'
                ),
                this.getNestedValue(
                    json,
                    'date'
                ),
            ], null);
        },

        getCustomerName(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                this.getNestedValue(
                    bill,
                    'billing_data.business_name'
                ),
                this.getNestedValue(
                    bill,
                    'billing_data.name'
                ),
                this.getNestedValue(
                    json,
                    'customer.legal_name'
                ),
                bill && bill.company_name,
            ], 'Sin receptor');
        },

        getCustomerRfc(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                this.getNestedValue(
                    bill,
                    'billing_data.rfc'
                ),
                this.getNestedValue(
                    json,
                    'customer.tax_id'
                ),
            ], '—');
        },

        getCurrency(bill) {
            const json =
                this.getInvoiceJson(bill);

            const currency =
                this.firstValue([
                    bill && bill.currency,
                    this.getNestedValue(
                        json,
                        'currency'
                    ),
                ], '');

            if (currency) {
                return String(
                    currency
                ).toUpperCase();
            }

            return Number(
                bill && bill.currency_id
            ) === 2
                ? 'USD'
                : 'MXN';
        },

        getExchangeRate(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.numeric(
                this.firstValue([
                    bill &&
                        bill.exchange_rate_bill,

                    this.getNestedValue(
                        json,
                        'exchange_rate'
                    ),

                    this.getNestedValue(
                        json,
                        'exchange_rate_bill'
                    ),
                ], 0)
            );
        },

        getPaymentMethod(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                bill && bill.payment_method,
                this.getNestedValue(
                    json,
                    'payment_method'
                ),
            ], '—');
        },

        getPaymentMethodLabel(bill) {
            const method =
                this.getPaymentMethod(bill);

            const labels = {
                PUE:
                    'PUE · Pago en una sola exhibición',

                PPD:
                    'PPD · Pago en parcialidades o diferido',
            };

            return labels[method] ||
                method ||
                '—';
        },

        getPaymentForm(bill) {
            const json =
                this.getInvoiceJson(bill);

            return this.firstValue([
                bill && bill.payment_form,
                this.getNestedValue(
                    json,
                    'payment_form'
                ),
            ], '');
        },

        getPaymentFormLabel(bill) {
            const form =
                String(
                    this.getPaymentForm(
                        bill
                    ) || ''
                );

            const labels = {
                '01':
                    '01 · Efectivo',

                '02':
                    '02 · Cheque nominativo',

                '03':
                    '03 · Transferencia electrónica',

                '04':
                    '04 · Tarjeta de crédito',

                '28':
                    '28 · Tarjeta de débito',

                '99':
                    '99 · Por definir',
            };

            return labels[form] ||
                form ||
                '—';
        },

        getInvoiceStatus(bill) {
            const json =
                this.getInvoiceJson(bill);

            const status =
                String(
                    this.firstValue([
                        bill && bill.status,
                        this.getNestedValue(
                            json,
                            'status'
                        ),
                    ], 'valid')
                )
                    .toLowerCase();

            if (
                [
                    'canceled',
                    'cancelled',
                    'cancelado',
                    'canceled_by_sat'
                ].includes(status)
            ) {
                return 'canceled';
            }

            if (
                [
                    'pending',
                    'pending_cancel',
                    'pending_cancellation'
                ].includes(status)
            ) {
                return 'pending';
            }

            return 'valid';
        },

        getInvoiceStatusText(bill) {
            const labels = {
                valid:
                    'Vigente',

                canceled:
                    'Cancelada',

                pending:
                    'Cancelación pendiente',
            };

            return labels[
                this.getInvoiceStatus(
                    bill
                )
            ] || 'Vigente';
        },

        getInvoiceStatusIcon(bill) {
            const icons = {
                valid:
                    'fa-solid fa-circle-check',

                canceled:
                    'fa-solid fa-ban',

                pending:
                    'fa-solid fa-clock',
            };

            return icons[
                this.getInvoiceStatus(
                    bill
                )
            ];
        },

        getInvoiceStatusClass(bill) {
            const status =
                this.getInvoiceStatus(
                    bill
                );

            return {
                'invoice-status--valid':
                    status === 'valid',

                'invoice-status--canceled':
                    status === 'canceled',

                'invoice-status--pending':
                    status === 'pending',
            };
        },

        isCanceled(bill) {
            return this.getInvoiceStatus(
                bill
            ) === 'canceled';
        },


        /*
         * ========================================
         * ITEMS / IMPUESTOS
         * ========================================
         */

        getItems(bill) {
            if (
                bill &&
                Array.isArray(
                    bill.items
                )
            ) {
                return bill.items;
            }

            return [];
        },

        taxEnabled(value) {
            if (
                value === true ||
                value === 'true'
            ) {
                return true;
            }

            return this.numeric(
                value
            ) > 0;
        },

        getItemAmount(item) {
            return this.numeric(
                item &&
                    item.final_amount
            );
        },

        getItemDiscount(item) {
            return Math.max(
                0,
                this.numeric(
                    item &&
                        item.discount
                )
            );
        },

        getItemTaxCalculation(item) {
            const amount =
                this.getItemAmount(
                    item
                );

            const discount =
                this.getItemDiscount(
                    item
                );

            const subtotal =
                Math.max(
                    0,
                    amount -
                    discount
                );

            const ieps =
                this.taxEnabled(
                    item &&
                        item.ieps
                )
                    ? subtotal * 0.08
                    : 0;

            const taxBase =
                subtotal +
                ieps;

            const iva =
                this.taxEnabled(
                    item &&
                        item.iva
                )
                    ? taxBase * 0.16
                    : 0;

            const isrRet =
                this.taxEnabled(
                    item &&
                        item.isr_ret
                )
                    ? taxBase * 0.10
                    : 0;

            const ivaRet =
                this.taxEnabled(
                    item &&
                        item.iva_ret
                )
                    ? taxBase * 0.106667
                    : 0;

            return {
                amount,
                discount,
                subtotal,
                ieps,
                taxBase,
                iva,
                isrRet,
                ivaRet,

                total:
                    subtotal +
                    ieps +
                    iva -
                    isrRet -
                    ivaRet,
            };
        },

        getCalculatedTotals(bill) {
            return this
                .getItems(bill)
                .reduce(
                    (totals, item) => {
                        const row =
                            this.getItemTaxCalculation(
                                item
                            );

                        totals.amount +=
                            row.amount;

                        totals.discount +=
                            row.discount;

                        totals.subtotal +=
                            row.subtotal;

                        totals.ieps +=
                            row.ieps;

                        totals.iva +=
                            row.iva;

                        totals.isrRet +=
                            row.isrRet;

                        totals.ivaRet +=
                            row.ivaRet;

                        totals.total +=
                            row.total;

                        return totals;
                    },
                    {
                        amount: 0,
                        discount: 0,
                        subtotal: 0,
                        ieps: 0,
                        iva: 0,
                        isrRet: 0,
                        ivaRet: 0,
                        total: 0,
                    }
                );
        },

        getInvoiceSubtotal(bill) {
            const json =
                this.getInvoiceJson(bill);

            const direct =
                this.firstValue([
                    bill && bill.subtotal,
                    this.getNestedValue(
                        json,
                        'subtotal'
                    ),
                ], null);

            if (
                direct !== null
            ) {
                return this.numeric(
                    direct
                );
            }

            return this
                .getCalculatedTotals(
                    bill
                )
                .subtotal;
        },

        getInvoiceTotal(bill) {
            const json =
                this.getInvoiceJson(bill);

            const direct =
                this.firstValue([
                    bill && bill.total,
                    this.getNestedValue(
                        json,
                        'total'
                    ),
                ], null);

            if (
                direct !== null
            ) {
                return this.numeric(
                    direct
                );
            }

            return this
                .getCalculatedTotals(
                    bill
                )
                .total;
        },


        /*
         * ========================================
         * COMPLEMENTO DE PAGO
         * ========================================
         */

        setPaymentAddOn(
            number = false,
            edit = true
        ) {
            this.prop_edit_child =
                edit;

            Event.$emit(
                'event-show-addPaymentAddOn',
                number
            );
        },


        /*
         * ========================================
         * CANCELACIÓN
         * ========================================
         */

        async deleteBill(bill) {
            if (
                !bill ||
                !bill.id ||
                this.isCanceled(bill)
            ) {
                return;
            }

            const result =
                await this.$swal({
                    title:
                        'Cancelar factura',

                    text:
                        'Esta acción solicitará la cancelación de la factura. Verifica que sea el CFDI correcto antes de continuar.',

                    icon:
                        'warning',

                    showCancelButton:
                        true,

                    confirmButtonColor:
                        '#d84d5d',

                    cancelButtonColor:
                        '#6c757d',

                    confirmButtonText:
                        'Sí, cancelar factura',

                    cancelButtonText:
                        'Regresar',

                    reverseButtons:
                        true,
                });

            if (
                !result.isConfirmed
            ) {
                return;
            }

            this.send = true;

            this.cancellingBillId =
                bill.id;

            const endpoint =
                '/external_bill/' +
                bill.id;

            try {
                const response =
                    await request.destroy(
                        endpoint
                    );

                this.successNotification(
                    response.data.message
                );

                await this.list();

                Event.$emit(
                    'event-load-contract_dues'
                );
            } catch (error) {
                this.errors =
                    await this.errorNotification(
                        error
                    );
            } finally {
                this.send = false;

                this.cancellingBillId =
                    null;
            }
        },


        /*
         * ========================================
         * LISTADO
         * ========================================
         */

        async list() {
            if (
                !this.debt ||
                !this.debt.id
            ) {
                this.bills = [];
                return;
            }

            this.load = true;

            this.errors = {};

            const endpoint =
                '/external_bill' +
                '?contract_dues_id=' +
                this.debt.id;

            try {
                const { data } =
                    await request.get(
                        endpoint
                    );

                if (
                    Array.isArray(data)
                ) {
                    this.bills =
                        data;

                } else if (
                    data &&
                    Array.isArray(
                        data.data
                    )
                ) {
                    this.bills =
                        data.data;

                } else {
                    this.bills = [];
                }
            } catch (error) {
                this.bills = [];

                this.errors =
                    await this.errorNotification(
                        error
                    );
            } finally {
                this.load = false;
            }
        },


        /*
         * ========================================
         * MODAL
         * ========================================
         */

        openModal(debt) {
            this.debt =
                debt || {};

            this.$modal.show(
                'bill-external-dev'
            );

            this.list();
        },

        refreshList() {
            this.list();
        },

        closedModal() {
            this.bills = [];

            this.debt = {};

            this.send = false;

            this.cancellingBillId =
                null;

            this.errors = {};

            this.$modal.hide(
                'bill-external-dev'
            );
        },


        /*
         * ========================================
         * NOTIFICACIONES
         * ========================================
         */

        ...mapActions({
            errorNotification:
                'alert/error',

            successNotification:
                'alert/success',
        }),
    },

    created() {
        Event.$on(
            'event-show-bill-external-dev',
            this.openModal
        );

        Event.$on(
            'event-show-bill-external-debv-list',
            this.refreshList
        );
    },

    beforeDestroy() {
        Event.$off(
            'event-show-bill-external-dev',
            this.openModal
        );

        Event.$off(
            'event-show-bill-external-debv-list',
            this.refreshList
        );
    },
};
</script>

<template>
    <modal
        name="bill-external-dev"
        :width="'92%'"
        height="auto"
        :adaptive="true"
        :scrollable="true"
        :click-to-close="false"
    >
        <div class="invoice-history-modal">
            <!-- HEADER -->
            <div class="invoice-history-modal__header">
                <div class="invoice-history-modal__title-group">
                    <div class="invoice-history-modal__icon">
                        <i class="fa-solid fa-file-invoice-dollar"></i>
                    </div>

                    <div>
                        <h4 class="invoice-history-modal__title">
                            Facturas del adeudo
                        </h4>

                        <p class="invoice-history-modal__subtitle mb-0">
                            Consulta los CFDI generados, conceptos, moneda,
                            tipo de cambio y archivos fiscales.
                        </p>
                    </div>
                </div>

                <div class="invoice-history-modal__header-actions">
                    <span
                        v-if="debt && debt.id"
                        class="invoice-history-modal__debt"
                    >
                        Adeudo #{{ debt.id }}
                    </span>

                    <button
                        type="button"
                        class="invoice-history-modal__close"
                        title="Cerrar"
                        aria-label="Cerrar"
                        @click="closedModal"
                    >
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>

            <!-- LOADING -->
            <div
                v-if="load"
                class="invoice-history-loading"
            >
                <div class="invoice-history-loading__spinner">
                    <i class="fas fa-circle-notch fa-spin"></i>
                </div>

                <h5 class="mb-1">
                    Cargando facturas
                </h5>

                <p class="mb-0">
                    Consultando la información fiscal del adeudo.
                </p>
            </div>

            <!-- EMPTY -->
            <div
                v-else-if="bills.length === 0"
                class="invoice-history-empty"
            >
                <div class="invoice-history-empty__icon">
                    <i class="fa-regular fa-file-lines"></i>
                </div>

                <h5>
                    Sin facturas
                </h5>

                <p class="mb-0">
                    Este adeudo todavía no tiene CFDI generados.
                </p>
            </div>

            <!-- BODY -->
            <template v-else>
                <div class="invoice-history-modal__body">
                    <!-- SUMMARY -->
                    <div class="invoice-history-summary">
                        <div class="invoice-history-summary__item">
                            <small>
                                Facturas generadas
                            </small>

                            <strong>
                                {{ bills.length }}
                            </strong>
                        </div>

                        <div class="invoice-history-summary__item">
                            <small>
                                Adeudo
                            </small>

                            <strong>
                                #{{ debt.id }}
                            </strong>
                        </div>

                        <div class="invoice-history-summary__item">
                            <small>
                                Contrato
                            </small>

                            <strong>
                                {{
                                    debt.contract_id
                                        ? '#' + debt.contract_id
                                        : '—'
                                }}
                            </strong>
                        </div>
                    </div>

                    <!-- FACTURAS -->
                    <div class="invoice-history-list">
                        <div
                            v-for="(bill, index) in bills"
                            :key="bill.id || index"
                            class="invoice-record"
                        >
                            <!-- Record header -->
                            <div class="invoice-record__header">
                                <div class="invoice-record__identity">
                                    <div class="invoice-record__number">
                                        {{ index + 1 }}
                                    </div>

                                    <div class="invoice-record__heading">
                                        <div class="invoice-record__title-row">
                                            <strong>
                                                Factura
                                                {{ getInvoiceSeries(bill) }}
                                                {{ getInvoiceFolio(bill) }}
                                            </strong>

                                            <span
                                                class="invoice-status"
                                                :class="getInvoiceStatusClass(bill)"
                                            >
                                                <i
                                                    class="mr-1"
                                                    :class="getInvoiceStatusIcon(bill)"
                                                ></i>

                                                {{ getInvoiceStatusText(bill) }}
                                            </span>
                                        </div>

                                        <small>
                                            UUID:
                                            <span class="invoice-uuid">
                                                {{ getInvoiceUuid(bill) }}
                                            </span>
                                        </small>
                                    </div>
                                </div>

                                <div
                                    v-if="getInvoiceDate(bill)"
                                    class="invoice-record__date"
                                >
                                    <small>
                                        Fecha de emisión
                                    </small>

                                    <strong>
                                        {{ getInvoiceDate(bill) | toDateTime }}
                                    </strong>
                                </div>
                            </div>

                            <div class="invoice-record__body">
                                <!-- Datos fiscales -->
                                <div class="invoice-info-grid">
                                    <div class="invoice-info-card invoice-info-card--wide">
                                        <span class="invoice-info-card__icon">
                                            <i class="fa-solid fa-user"></i>
                                        </span>

                                        <div>
                                            <small>
                                                Receptor
                                            </small>

                                            <strong>
                                                {{ getCustomerName(bill) }}
                                            </strong>

                                            <span>
                                                RFC {{ getCustomerRfc(bill) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="invoice-info-card">
                                        <span class="invoice-info-card__icon">
                                            <i class="fa-solid fa-money-bill-transfer"></i>
                                        </span>

                                        <div>
                                            <small>
                                                Moneda
                                            </small>

                                            <strong>
                                                {{ getCurrency(bill) }}
                                            </strong>
                                        </div>
                                    </div>

                                    <div class="invoice-info-card">
                                        <span class="invoice-info-card__icon">
                                            <i class="fa-solid fa-arrow-right-arrow-left"></i>
                                        </span>

                                        <div>
                                            <small>
                                                Tipo de cambio
                                            </small>

                                            <strong>
                                                <template v-if="getExchangeRate(bill) > 0">
                                                    {{ getExchangeRate(bill) | toCurrency }}
                                                </template>

                                                <template v-else>
                                                    —
                                                </template>
                                            </strong>
                                        </div>
                                    </div>

                                    <div class="invoice-info-card">
                                        <span class="invoice-info-card__icon">
                                            <i class="fa-solid fa-file-code"></i>
                                        </span>

                                        <div>
                                            <small>
                                                CFDI
                                            </small>

                                            <strong>
                                                Versión {{ getCfdiVersion(bill) }}
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <!-- Pago -->
                                <div class="invoice-payment-info">
                                    <div>
                                        <small>
                                            Método de pago
                                        </small>

                                        <strong>
                                            {{ getPaymentMethodLabel(bill) }}
                                        </strong>
                                    </div>

                                    <div>
                                        <small>
                                            Forma de pago
                                        </small>

                                        <strong>
                                            {{ getPaymentFormLabel(bill) }}
                                        </strong>
                                    </div>
                                </div>

                                <!-- Conceptos + resumen -->
                                <div class="row">
                                    <div class="col-lg-8 mb-3 mb-lg-0">
                                        <div class="invoice-items">
                                            <div class="invoice-items__header">
                                                <div>
                                                    <strong>
                                                        Conceptos facturados
                                                    </strong>

                                                    <small>
                                                        {{ getItems(bill).length }}
                                                        {{
                                                            getItems(bill).length === 1
                                                                ? 'concepto'
                                                                : 'conceptos'
                                                        }}
                                                    </small>
                                                </div>
                                            </div>

                                            <div
                                                v-if="getItems(bill).length === 0"
                                                class="invoice-items__empty"
                                            >
                                                Sin conceptos registrados.
                                            </div>

                                            <div
                                                v-else
                                                class="table-responsive"
                                            >
                                                <table class="table invoice-items-table">
                                                    <thead>
                                                        <tr>
                                                            <th class="invoice-item-index">
                                                                #
                                                            </th>

                                                            <th>
                                                                Concepto
                                                            </th>

                                                            <th class="text-right">
                                                                Importe
                                                            </th>

                                                            <th class="text-right">
                                                                Descuento
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        <tr
                                                            v-for="(item, itemIndex) in getItems(bill)"
                                                            :key="item.id || itemIndex"
                                                        >
                                                            <td class="invoice-item-index">
                                                                {{ itemIndex + 1 }}
                                                            </td>

                                                            <td>
                                                                <div class="invoice-item-description">
                                                                    <strong>
                                                                        {{ item.description }}
                                                                    </strong>

                                                                    <div class="invoice-item-taxes">
                                                                        <span
                                                                            v-if="taxEnabled(item.iva)"
                                                                            class="invoice-tax-chip"
                                                                        >
                                                                            IVA 16%
                                                                        </span>

                                                                        <span
                                                                            v-if="taxEnabled(item.ieps)"
                                                                            class="invoice-tax-chip"
                                                                        >
                                                                            IEPS 8%
                                                                        </span>

                                                                        <span
                                                                            v-if="taxEnabled(item.isr_ret)"
                                                                            class="invoice-tax-chip invoice-tax-chip--retention"
                                                                        >
                                                                            ISR Ret.
                                                                        </span>

                                                                        <span
                                                                            v-if="taxEnabled(item.iva_ret)"
                                                                            class="invoice-tax-chip invoice-tax-chip--retention"
                                                                        >
                                                                            IVA Ret.
                                                                        </span>

                                                                        <span
                                                                            v-if="
                                                                                !taxEnabled(item.iva) &&
                                                                                !taxEnabled(item.ieps) &&
                                                                                !taxEnabled(item.isr_ret) &&
                                                                                !taxEnabled(item.iva_ret)
                                                                            "
                                                                            class="invoice-tax-chip invoice-tax-chip--none"
                                                                        >
                                                                            Sin impuestos
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td class="text-right">
                                                                <strong>
                                                                    {{ getItemAmount(item) | toCurrency }}
                                                                </strong>

                                                                <small class="invoice-currency-code">
                                                                    {{ getCurrency(bill) }}
                                                                </small>
                                                            </td>

                                                            <td class="text-right">
                                                                {{ getItemDiscount(item) | toCurrency }}

                                                                <small class="invoice-currency-code">
                                                                    {{ getCurrency(bill) }}
                                                                </small>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="col-lg-4">
                                        <div class="invoice-totals">
                                            <div class="invoice-totals__header">
                                                <i class="fas fa-calculator mr-2"></i>
                                                Resumen fiscal
                                            </div>

                                            <div class="invoice-totals__row">
                                                <span>
                                                    Subtotal
                                                </span>

                                                <strong>
                                                    {{ getInvoiceSubtotal(bill) | toCurrency }}
                                                    {{ getCurrency(bill) }}
                                                </strong>
                                            </div>

                                            <div
                                                v-if="getCalculatedTotals(bill).ieps > 0"
                                                class="invoice-totals__row"
                                            >
                                                <span>
                                                    IEPS
                                                </span>

                                                <strong>
                                                    + {{ getCalculatedTotals(bill).ieps | toCurrency }}
                                                </strong>
                                            </div>

                                            <div
                                                v-if="getCalculatedTotals(bill).iva > 0"
                                                class="invoice-totals__row"
                                            >
                                                <span>
                                                    IVA
                                                </span>

                                                <strong>
                                                    + {{ getCalculatedTotals(bill).iva | toCurrency }}
                                                </strong>
                                            </div>

                                            <div
                                                v-if="getCalculatedTotals(bill).isrRet > 0"
                                                class="invoice-totals__row invoice-totals__row--retention"
                                            >
                                                <span>
                                                    ISR retenido
                                                </span>

                                                <strong>
                                                    - {{ getCalculatedTotals(bill).isrRet | toCurrency }}
                                                </strong>
                                            </div>

                                            <div
                                                v-if="getCalculatedTotals(bill).ivaRet > 0"
                                                class="invoice-totals__row invoice-totals__row--retention"
                                            >
                                                <span>
                                                    IVA retenido
                                                </span>

                                                <strong>
                                                    - {{ getCalculatedTotals(bill).ivaRet | toCurrency }}
                                                </strong>
                                            </div>

                                            <div class="invoice-totals__total">
                                                <span>
                                                    Total
                                                </span>

                                                <strong>
                                                    {{ getInvoiceTotal(bill) | toCurrency }}
                                                    {{ getCurrency(bill) }}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div class="invoice-record__footer">
                                <div>
                                    <button
                                        v-if="!isCanceled(bill)"
                                        type="button"
                                        class="btn invoice-btn invoice-btn--danger"
                                        :disabled="send"
                                        @click="deleteBill(bill)"
                                    >
                                        <i
                                            v-if="cancellingBillId !== bill.id"
                                            class="fa-solid fa-ban mr-1"
                                        ></i>

                                        <i
                                            v-else
                                            class="fa-solid fa-circle-notch fa-spin mr-1"
                                        ></i>

                                        {{
                                            cancellingBillId === bill.id
                                                ? 'Cancelando...'
                                                : 'Cancelar factura'
                                        }}
                                    </button>
                                </div>

                                <div class="invoice-record__documents">
                                    <!-- <button
                                        type="button"
                                        class="btn invoice-btn invoice-btn--primary"
                                        @click="setPaymentAddOn(bill)"
                                    >
                                        <i class="fa-solid fa-money-bill-transfer mr-1"></i>
                                        Complemento de pago
                                    </button> -->

                                    <a
                                        target="_blank"
                                        rel="noopener"
                                        class="btn invoice-btn invoice-btn--pdf"
                                        :href="`${globalUrlFiles}/file/bill_external/${bill.id}/pdf`"
                                    >
                                        <i class="fas fa-file-pdf mr-1"></i>
                                        PDF
                                    </a>

                                    <a
                                        target="_blank"
                                        rel="noopener"
                                        class="btn invoice-btn invoice-btn--xml"
                                        :href="`${globalUrlFiles}/file/bill_external/${bill.id}/xml`"
                                    >
                                        <i class="fas fa-file-code mr-1"></i>
                                        XML
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- FOOTER -->
                <div class="invoice-history-modal__footer">
                    <div class="invoice-history-modal__footer-info">
                        <i class="fas fa-info-circle mr-2"></i>

                        Los importes se muestran en la moneda utilizada
                        al emitir cada factura.
                    </div>

                    <button
                        type="button"
                        class="btn invoice-btn invoice-btn--secondary"
                        @click="closedModal"
                    >
                        <i class="fas fa-arrow-left mr-1"></i>
                        Regresar
                    </button>
                </div>
            </template>

            <ModalPaymentAddOnComponent />
        </div>
    </modal>
</template>

<style scoped>
.invoice-history-modal {
    --primary: #5672a5;
    --primary-dark: #304d7d;
    --primary-soft: #eef3fb;
    --success: #269b74;
    --success-soft: #eaf8f3;
    --warning: #d99b24;
    --warning-soft: #fff8e8;
    --danger: #d84d5d;
    --danger-soft: #fff0f2;
    --border: #dfe5ee;
    --surface: #ffffff;
    --background: #f5f7fb;
    --text: #1f2937;
    --muted: #6b7280;

    min-height: 360px;

    color: var(--text);
    background: var(--background);
}


/* HEADER */

.invoice-history-modal__header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 18px;

    padding: 18px 22px;

    color: #ffffff;

    background:
        linear-gradient(
            135deg,
            var(--primary-dark),
            var(--primary)
        );
}


.invoice-history-modal__title-group {
    display: flex;

    align-items: center;

    gap: 14px;

    min-width: 0;
}


.invoice-history-modal__icon {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 46px;

    width: 46px;
    height: 46px;

    color: #ffffff;

    background:
        rgba(
            255,
            255,
            255,
            0.15
        );

    border:
        1px solid
        rgba(
            255,
            255,
            255,
            0.2
        );

    border-radius: 12px;

    
}


.invoice-history-modal__title {
    margin: 0;

    color: #ffffff;

    
    font-weight: 700;
}


.invoice-history-modal__subtitle {
    margin-top: 3px;

    color:
        rgba(
            255,
            255,
            255,
            0.78
        );

    
    line-height: 1.45;
}


.invoice-history-modal__header-actions {
    display: flex;

    align-items: center;

    gap: 10px;
}


.invoice-history-modal__debt {
    padding: 5px 9px;

    color: #ffffff;

    background:
        rgba(
            255,
            255,
            255,
            0.12
        );

    border:
        1px solid
        rgba(
            255,
            255,
            255,
            0.15
        );

    border-radius: 999px;

    
    font-weight: 700;
}


.invoice-history-modal__close {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 36px;

    width: 36px;
    height: 36px;

    padding: 0;

    color: #ffffff;

    background:
        rgba(
            255,
            255,
            255,
            0.12
        );

    border: 0;
    border-radius: 50%;

    cursor: pointer;

    transition: 0.2s ease;
}


.invoice-history-modal__close:hover {
    color: #ffffff;

    background:
        rgba(
            255,
            255,
            255,
            0.24
        );

    transform:
        rotate(90deg);
}


/* LOADING / EMPTY */

.invoice-history-loading,
.invoice-history-empty {
    display: flex;

    flex-direction: column;
    align-items: center;
    justify-content: center;

    min-height: 300px;

    padding: 45px 20px;

    color: var(--muted);

    text-align: center;
}


.invoice-history-loading__spinner,
.invoice-history-empty__icon {
    display: flex;

    align-items: center;
    justify-content: center;

    width: 52px;
    height: 52px;

    margin-bottom: 12px;

    color: var(--primary);

    background: var(--primary-soft);

    border-radius: 50%;

    
}


.invoice-history-loading h5,
.invoice-history-empty h5 {
    color: var(--text);

    
    font-weight: 700;
}


.invoice-history-loading p,
.invoice-history-empty p {
    
}


/* BODY */

.invoice-history-modal__body {
    max-height:
        calc(
            100vh - 190px
        );

    overflow-y: auto;

    padding: 20px 22px;
}


.invoice-history-summary {
    display: grid;

    grid-template-columns:
        repeat(
            3,
            minmax(
                0,
                1fr
            )
        );

    gap: 10px;

    margin-bottom: 14px;
}


.invoice-history-summary__item {
    padding: 10px 12px;

    background: #ffffff;

    border:
        1px solid
        var(--border);

    border-radius: 9px;
}


.invoice-history-summary__item small,
.invoice-history-summary__item strong {
    display: block;
}


.invoice-history-summary__item small {
    margin-bottom: 2px;

    color: var(--muted);

    

    text-transform: uppercase;
}


.invoice-history-summary__item strong {
    color: var(--text);
}


/* LIST */

.invoice-history-list {
    display: grid;

    gap: 14px;
}


.invoice-record {
    overflow: hidden;

    background: var(--surface);

    border:
        1px solid
        var(--border);

    border-radius: 12px;

    box-shadow:
        0
        3px
        10px
        rgba(
            15,
            23,
            42,
            0.04
        );
}


.invoice-record__header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 14px;

    padding: 13px 15px;

    background: #fafbfd;

    border-bottom:
        1px solid
        var(--border);
}


.invoice-record__identity {
    display: flex;

    align-items: center;

    gap: 11px;

    min-width: 0;
}


.invoice-record__number {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 34px;

    width: 34px;
    height: 34px;

    color: var(--primary-dark);

    background: var(--primary-soft);

    border-radius: 8px;

    
    font-weight: 700;
}


.invoice-record__heading {
    min-width: 0;
}


.invoice-record__title-row {
    display: flex;

    flex-wrap: wrap;
    align-items: center;

    gap: 8px;
}


.invoice-record__title-row strong {
    color: var(--text);

    
}


.invoice-record__heading > small {
    display: block;

    margin-top: 3px;

    color: var(--muted);

    
}


.invoice-uuid {
    word-break: break-all;
}


.invoice-record__date {
    flex: 0 0 auto;

    text-align: right;
}


.invoice-record__date small,
.invoice-record__date strong {
    display: block;
}


.invoice-record__date small {
    color: var(--muted);

    
}


.invoice-record__date strong {
    margin-top: 2px;

    color: var(--text);

    
}


/* STATUS */

.invoice-status {
    display: inline-flex;

    align-items: center;

    padding: 4px 7px;

    border-radius: 999px;

    
    font-weight: 700;
}


.invoice-status--valid {
    color: var(--success);

    background: var(--success-soft);
}


.invoice-status--canceled {
    color: var(--danger);

    background: var(--danger-soft);
}


.invoice-status--pending {
    color: #946b10;

    background: var(--warning-soft);
}


/* RECORD BODY */

.invoice-record__body {
    padding: 15px;
}


.invoice-info-grid {
    display: grid;

    grid-template-columns:
        repeat(
            5,
            minmax(
                0,
                1fr
            )
        );

    gap: 9px;

    margin-bottom: 11px;
}


.invoice-info-card {
    display: flex;

    align-items: center;

    gap: 9px;

    min-width: 0;

    padding: 9px 10px;

    background: #f8fafc;

    border:
        1px solid
        #e8ecf1;

    border-radius: 8px;
}


.invoice-info-card--wide {
    grid-column:
        span 2;
}


.invoice-info-card__icon {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 30px;

    width: 30px;
    height: 30px;

    color: var(--primary);

    background: #ffffff;

    border-radius: 7px;

    
}


.invoice-info-card > div {
    min-width: 0;
}


.invoice-info-card small,
.invoice-info-card strong,
.invoice-info-card div > span {
    display: block;
}


.invoice-info-card small {
    color: var(--muted);

    

    text-transform: uppercase;
}


.invoice-info-card strong {
    overflow: hidden;

    margin-top: 1px;

    color: var(--text);

    

    text-overflow: ellipsis;
    white-space: nowrap;
}


.invoice-info-card div > span {
    overflow: hidden;

    margin-top: 1px;

    color: var(--muted);

    

    text-overflow: ellipsis;
    white-space: nowrap;
}


/* PAYMENT */

.invoice-payment-info {
    display: grid;

    grid-template-columns:
        repeat(
            2,
            minmax(
                0,
                1fr
            )
        );

    gap: 9px;

    margin-bottom: 13px;

    padding: 10px 12px;

    background: #fbfcfd;

    border:
        1px solid
        var(--border);

    border-radius: 8px;
}


.invoice-payment-info small,
.invoice-payment-info strong {
    display: block;
}


.invoice-payment-info small {
    color: var(--muted);

    

    text-transform: uppercase;
}


.invoice-payment-info strong {
    margin-top: 2px;

    color: var(--text);

    
}


/* ITEMS */

.invoice-items {
    height: 100%;

    overflow: hidden;

    border:
        1px solid
        var(--border);

    border-radius: 9px;
}


.invoice-items__header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    padding: 9px 12px;

    background: #fafbfd;

    border-bottom:
        1px solid
        var(--border);
}


.invoice-items__header strong,
.invoice-items__header small {
    display: block;
}


.invoice-items__header strong {
    color: var(--text);

    
}


.invoice-items__header small {
    margin-top: 1px;

    color: var(--muted);

    
}


.invoice-items__empty {
    padding: 30px 15px;

    color: var(--muted);

    

    text-align: center;
}


.invoice-items-table {
    margin: 0;

    
}


.invoice-items-table thead th {
    padding: 8px 10px;

    color: #4b5563;

    background: #f8fafc;

    border-top: 0;
    border-bottom:
        1px solid
        var(--border);

    

    text-transform: uppercase;
}


.invoice-items-table tbody td {
    padding: 9px 10px;

    vertical-align: middle;

    border-top:
        1px solid
        #eef1f4;
}


.invoice-item-index {
    width: 45px;

    color: var(--muted);

    text-align: center;
}


.invoice-item-description strong {
    display: block;

    color: var(--text);

    
}


.invoice-item-taxes {
    display: flex;

    flex-wrap: wrap;

    gap: 4px;

    margin-top: 5px;
}


.invoice-tax-chip {
    display: inline-flex;

    padding: 2px 5px;

    color: var(--primary-dark);

    background: var(--primary-soft);

    border-radius: 999px;

    
    font-weight: 600;
}


.invoice-tax-chip--retention {
    color: #a23845;

    background: var(--danger-soft);
}


.invoice-tax-chip--none {
    color: var(--muted);

    background: #f1f3f5;
}


.invoice-currency-code {
    display: block;

    margin-top: 1px;

    color: var(--muted);

    
}


/* TOTALS */

.invoice-totals {
    overflow: hidden;

    border:
        1px solid
        var(--border);

    border-radius: 9px;
}


.invoice-totals__header {
    display: flex;

    align-items: center;

    padding: 9px 11px;

    color: #ffffff;

    background: var(--primary-dark);

    
    font-weight: 700;
}


.invoice-totals__row,
.invoice-totals__total {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 10px;

    padding: 8px 11px;

    border-bottom:
        1px solid
        #edf0f4;

    
}


.invoice-totals__row span {
    color: var(--muted);
}


.invoice-totals__row strong {
    color: var(--text);

    
}


.invoice-totals__row--retention {
    background: #fffafb;
}


.invoice-totals__row--retention strong {
    color: var(--danger);
}


.invoice-totals__total {
    border-bottom: 0;

    color: var(--primary-dark);

    background: var(--primary-soft);
}


.invoice-totals__total span {
    font-weight: 600;
}


.invoice-totals__total strong {
    color: var(--primary-dark);

    
}


/* FOOTER RECORD */

.invoice-record__footer {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 10px;

    padding: 11px 15px;

    background: #fafbfd;

    border-top:
        1px solid
        var(--border);
}


.invoice-record__documents {
    display: flex;

    flex-wrap: wrap;

    gap: 6px;

    justify-content: flex-end;
}


/* BUTTONS */

.invoice-btn {
    min-height: 34px;

    padding: 6px 10px;

    border-radius: 6px;

    
    font-weight: 600;
}


.invoice-btn--primary {
    color: var(--primary-dark);

    background: var(--primary-soft);

    border:
        1px solid
        #d8e2f1;
}


.invoice-btn--primary:hover {
    color: #ffffff;

    background: var(--primary);

    border-color: var(--primary);
}


.invoice-btn--pdf {
    color: #a23845;

    background: #fff8f9;

    border:
        1px solid
        #f2d4d9;
}


.invoice-btn--pdf:hover {
    color: #ffffff;

    background: #b94958;

    border-color: #b94958;
}


.invoice-btn--xml {
    color: #4b5563;

    background: #ffffff;

    border:
        1px solid
        var(--border);
}


.invoice-btn--xml:hover {
    color: var(--text);

    background: #f3f5f8;
}


.invoice-btn--danger {
    color: var(--danger);

    background: #ffffff;

    border:
        1px solid
        #ecc8cd;
}


.invoice-btn--danger:hover {
    color: #ffffff;

    background: var(--danger);

    border-color: var(--danger);
}


.invoice-btn--secondary {
    min-width: 110px;

    color: #4b5563;

    background: #ffffff;

    border:
        1px solid
        var(--border);
}


.invoice-btn--secondary:hover {
    color: var(--text);

    background: #f3f5f8;
}


/* MAIN FOOTER */

.invoice-history-modal__footer {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 12px;

    padding: 13px 22px;

    background: #ffffff;

    border-top:
        1px solid
        var(--border);
}


.invoice-history-modal__footer-info {
    display: flex;

    align-items: center;

    color: var(--muted);

    
}


.invoice-history-modal__footer-info i {
    color: var(--primary);
}


/* RESPONSIVE */

@media (max-width: 1200px) {

    .invoice-info-grid {
        grid-template-columns:
            repeat(
                3,
                minmax(
                    0,
                    1fr
                )
            );
    }


    .invoice-info-card--wide {
        grid-column:
            span 2;
    }
}


@media (max-width: 768px) {

    .invoice-history-modal__header {
        padding: 15px;
    }


    .invoice-history-modal__subtitle,
    .invoice-history-modal__debt {
        display: none;
    }


    .invoice-history-modal__body {
        padding: 13px;
    }


    .invoice-history-summary {
        grid-template-columns:
            1fr;
    }


    .invoice-record__header {
        align-items: flex-start;

        flex-direction: column;
    }


    .invoice-record__date {
        margin-left: 45px;

        text-align: left;
    }


    .invoice-info-grid {
        grid-template-columns:
            1fr;
    }


    .invoice-info-card--wide {
        grid-column:
            span 1;
    }


    .invoice-payment-info {
        grid-template-columns:
            1fr;
    }


    .invoice-record__footer,
    .invoice-history-modal__footer {
        align-items: stretch;

        flex-direction: column;
    }


    .invoice-record__documents {
        flex-direction: column;
    }


    .invoice-record__documents .invoice-btn,
    .invoice-record__footer > div:first-child .invoice-btn,
    .invoice-history-modal__footer .invoice-btn {
        width: 100%;
    }


    .invoice-history-modal__footer-info {
        display: none;
    }
}
</style>
